/**
 * The run, visible from wherever you are.
 *
 * There were two run interfaces. The Pipeline tab had a progress bar, an ETA
 * and a stage table; every other page — including the DWI page, where the work
 * is actually looked at — had nothing, so watching a run meant leaving the
 * images. Two interfaces for one thing, and the one you were most likely to be
 * standing in front of was the one that told you least.
 *
 * This is the half that belongs everywhere: how far along, how long it has
 * taken, how long is left, which stage, and Stop. It sits above the workspace
 * and appears only while something is running, so it costs nothing the rest of
 * the time. The Pipeline tab keeps the stage table, which is the detail view —
 * but nobody has to go there to answer "is it still going".
 */

import { Square } from "lucide-react";

import { Button, Progress } from "@/components/ui/primitives";
import { cn, formatDuration } from "@/lib/utils";
import { useAppStore, useCurrentCase, useJob, useRunningJobs } from "@/store/appStore";
import { usePipelineSession } from "./session";
import { currentStageStatus, estimateEtaSec, jobElapsedSec, weightedProgressPct } from "./progress";
import { StageHealthNote } from "./status";
import { timingsFor, useStageTimings } from "./timings";
import { useNow } from "./useNow";

export function RunBar() {
  const job = useJob();
  const others = useRunningJobs().filter((j) => j.jobId !== job?.jobId);
  const subjectId = useCurrentCase()?.subjectId ?? null;
  const timings = useStageTimings((s) => s.timings);
  const stop = usePipelineSession((s) => s.stop);
  const batchTotal = usePipelineSession((s) => s.batchTotal);
  const batchIndex = usePipelineSession((s) => s.batchIndex);
  const focusJob = useAppStore((s) => s.focusJob);
  const running = job?.state === "running" || job?.state === "queued";
  // The clock only ticks while something is running; `useNow` must still be
  // called every render, so the interval is disabled rather than the hook.
  const now = useNow(1000, running);

  // Still shown while the focus run has ended but a sibling has not: the batch
  // is not over, and a bar that vanished would say it was.
  if (!job || (!running && others.length === 0)) return null;

  const pct = weightedProgressPct(job, timings, now, job.deviceClass);
  const eta = estimateEtaSec(job, timings, now);
  const elapsed = jobElapsedSec(job, now);
  const current = currentStageStatus(job);
  const done = job.stages.filter((s) => s.state === "completed").length;

  return (
    <div className="shrink-0 border-b border-ads-border bg-ads-surface px-4 py-2">
      <div className="flex items-center gap-4">
        <span className="flex min-w-0 shrink-0 items-baseline gap-2">
          {/* The subject, not the case id: a uuid tells the reader nothing
              about which of their subjects is on the machine right now. From the
              job rather than the open case, since in a parallel batch the run in
              focus need not be the case on screen. */}
          <span className="truncate text-[12px] font-semibold text-neutral-900">
            {job.subjectId || subjectId || job.caseId.slice(0, 8)}
          </span>
          {batchTotal > 1 ? (
            // `batchIndex` counts the one being run, 1-based: it is set to 1
            // when the first of the queue starts.
            <span className="shrink-0 text-[10.5px] tabular-nums text-neutral-500">
              subject {Math.min(batchIndex, batchTotal)} of {batchTotal}
            </span>
          ) : null}
        </span>

        <Progress value={pct} className="h-1.5 min-w-0 flex-1" />

        <span className="flex shrink-0 items-baseline gap-4 text-[11px] tabular-nums text-neutral-600">
          <span className="w-10 text-right font-medium text-neutral-900">{pct}%</span>
          <span title="Elapsed">{formatDuration(elapsed)}</span>
          {/* No estimate until a stage has finished: a countdown made up from
              nothing is worse than an em dash. */}
          <span title="Estimated time remaining" className={cn(eta == null && "text-neutral-300")}>
            {eta == null ? "—" : `${formatDuration(eta)} left`}
          </span>
          <span className="w-44 truncate text-neutral-500" title={current?.title}>
            {current ? `${current.modality} · ${current.title}` : `${done}/${job.stages.length}`}
          </span>
        </span>

        <Button size="sm" variant="danger" className="shrink-0" onClick={() => void stop()}>
          <Square className="h-3 w-3" />
          {/* Stop takes everything in flight, so it says so when that is more
              than one — a button that quietly stopped one of three would be
              read as a button that did not work. */}
          {others.length > 0 ? `Stop all ${others.length + 1}` : "Stop"}
        </Button>
      </div>

      {/* The other subjects of a parallel batch. One line each, clickable,
          because the bar shows one run in detail and the rest would otherwise be
          invisible — a second subject with no row anywhere looks frozen. */}
      {others.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 border-t border-ads-border pt-1.5">
          {others.map((other) => (
            <button
              key={other.jobId}
              type="button"
              onClick={() => focusJob(other.jobId)}
              title="Follow this subject instead"
              className="flex items-baseline gap-2 text-[11px] text-neutral-500 hover:text-neutral-900"
            >
              <span className="font-medium">{other.subjectId || other.caseId.slice(0, 8)}</span>
              <span className="tabular-nums">
                {other.state === "queued"
                  ? "waiting for a slot"
                  : // That run's own timings, not the focus run's: scoring one
                    // subject's progress against another's stage durations makes
                    // the percentage jump around.
                    `${weightedProgressPct(other, timingsFor(other.jobId), now, other.deviceClass)}%`}
              </span>
              <span className="truncate text-neutral-400">
                {currentStageStatus(other)?.title ?? ""}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* Only present while a stage is silent; see health.rs. */}
      {job.health ? (
        <div className="mt-1.5">
          <StageHealthNote health={job.health} />
        </div>
      ) : null}
    </div>
  );
}
